import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { cn } from '../../utils/cn';
import { cva } from 'class-variance-authority';
const statsCardVariants = cva('relative p-6 rounded-lg border transition-shadow duration-200', {
    variants: {
        variant: {
            default: 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md',
            colored: 'shadow-sm hover:shadow-md',
            outlined: 'bg-transparent border-2',
            filled: 'border-transparent shadow-sm hover:shadow-md',
        },
        color: {
            default: '',
            blue: '',
            green: '',
            red: '',
            yellow: '',
            purple: '',
            gray: '',
        },
    },
    compoundVariants: [
        // Colored variant styles
        {
            variant: 'colored',
            color: 'blue',
            className: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
        },
        {
            variant: 'colored',
            color: 'green',
            className: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800',
        },
        {
            variant: 'colored',
            color: 'red',
            className: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800',
        },
        {
            variant: 'colored',
            color: 'yellow',
            className: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
        },
        {
            variant: 'colored',
            color: 'purple',
            className: 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800',
        },
        // Outlined variant styles
        {
            variant: 'outlined',
            color: 'blue',
            className: 'border-blue-500 dark:border-blue-400',
        },
        {
            variant: 'outlined',
            color: 'green',
            className: 'border-green-500 dark:border-green-400',
        },
        // Filled variant styles
        {
            variant: 'filled',
            color: 'blue',
            className: 'bg-blue-500 text-white',
        },
        {
            variant: 'filled',
            color: 'green',
            className: 'bg-green-500 text-white',
        },
    ],
    defaultVariants: {
        variant: 'default',
        color: 'default',
    },
});
// Refined stats card for displaying KPIs and statistics
const StatsCard = React.forwardRef(({ className, title, value, change, trend = 'neutral', icon, description, variant, color, ...props }, ref) => {
    const trendColors = {
        up: 'text-green-600 dark:text-green-500',
        down: 'text-red-600 dark:text-red-500',
        neutral: 'text-gray-600 dark:text-gray-400'
    };
    const trendIcons = {
        up: '↑',
        down: '↓',
        neutral: '→'
    };
    const iconColors = {
        default: 'text-gray-600 dark:text-gray-400',
        blue: 'text-blue-600 dark:text-blue-400',
        green: 'text-green-600 dark:text-green-400',
        red: 'text-red-600 dark:text-red-400',
        yellow: 'text-amber-600 dark:text-amber-400',
        purple: 'text-purple-600 dark:text-purple-400',
        gray: 'text-gray-600 dark:text-gray-400',
    };
    return (_jsx("div", { ref: ref, className: cn(statsCardVariants({ variant, color }), className), ...props, children: _jsxs("div", { className: "flex items-start justify-between", children: [_jsxs("div", { className: "flex-1", children: [_jsx("p", { className: "text-sm font-medium text-gray-600 dark:text-gray-400", children: title }), _jsx("p", { className: "text-2xl font-semibold text-gray-900 dark:text-white mt-1", children: value }), description && (_jsx("p", { className: "text-xs text-gray-500 dark:text-gray-400 mt-1", children: description })), change && (_jsxs("div", { className: cn('text-sm mt-2 flex items-center gap-1', trendColors[trend]), children: [_jsx("span", { className: "font-medium", children: trendIcons[trend] }), _jsx("span", { children: change })] }))] }), icon && (_jsx("div", { className: cn('p-2 rounded-lg bg-gray-100 dark:bg-gray-800', color && color in iconColors ? iconColors[color] : ''), children: icon }))] }) }));
});
StatsCard.displayName = 'StatsCard';
export { StatsCard };
